import { useCallback, useMemo } from 'react';

import AutocompleteWithChips from 'components/AutocompleteWithChips/AutocompleteWithChips';
import { LookupDateRangeQueryItemDto } from 'types/CodegenLookupTypes';
import { SelectedItemDto } from 'types/CodegenReportTypes';
import { usePostLookupBatters } from 'hooks/CodegenLookupHooks';
import { useTranslation } from 'react-i18next';

type Props = {
  selectedBatters: SelectedItemDto[] | undefined;
  onChange: (batters: SelectedItemDto[]) => void;
  dateRanges: LookupDateRangeQueryItemDto[] | undefined;
  levelId: string | undefined;
  teamId: string | undefined;
};

const SelectBatters = ({ selectedBatters, onChange, dateRanges, levelId, teamId }: Props) => {
  const { t } = useTranslation();
  const { data, isLoading } = usePostLookupBatters(dateRanges, levelId, teamId);

  const allBatterIds = useMemo(() => data?.map((datum) => datum.id) ?? [], [data]);
  const getBatterName = useCallback(
    (batterId: string) => new Map(data?.map((datum) => [datum.id, datum.name])).get(batterId) ?? (t('Nonexistent batter') as string),
    [data]
  );

  return (
    <AutocompleteWithChips
      label={t('Batters')}
      options={allBatterIds}
      selected={selectedBatters?.map((batter) => batter.id) ?? []}
      setSelected={(selectedIds) =>
        onChange(selectedIds.map((id) => data?.find((datum) => datum.id === id)).filter(Boolean) as SelectedItemDto[])
      }
      getOptionLabel={(option) => (isLoading ? t('Loading batters...') : getBatterName(option))}
      noOptionsText={isLoading ? t('Loading batters...') : t('No batters found')}
      disabled={!dateRanges || !levelId || !teamId}
    />
  );
};

export default SelectBatters;
